import { Component, Element, h, Prop, State, Watch, Host, Method, Event, EventEmitter } from "@stencil/core"
import { govErrorLog } from "../../helpers/Log/gov.log"
import { validateProp } from "../../helpers/Validate/props"
import { SizeType, Sizes, AccordionItemClass, VariantType } from "./constants"
import { createID } from "../../utils/string.utils"
import { booleanString, toBoolAttrIfDefined } from "../../helpers/Dom/template"
import { GovAccordionItemChangeEvent } from "./gov-accordion-item.d"
import { Variants } from "../../core/constants/variants"

@Component({
	tag: "gov-accordion-item",
	styleUrl: "gov-accordion-item.scss",
})
export class GovAccordionItem {
	private readonly triggerId: string
	private readonly contentId: string

	constructor() {
		this.triggerId = createID("GovAccordionItem")
		this.contentId = createID("GovAccordionContent")
	}

	@Element() readonly host: HTMLGovAccordionItemElement
	@State() isExpandedState: boolean

	/**
	 * Style variation of the button.
	 */
	@Prop() readonly variant: VariantType = Variants.PRIMARY
	/**
	 * Custom accordion item identifier. Otherwise, it will be generated
	 */
	@Prop() readonly identifier: string
	/**
	 * Accordion’s size.
	 */
	@Prop() readonly size: SizeType = undefined
	/**
	 * Item name of the accordion
	 */
	@Prop() readonly label: string
	/**
	 * Makes the button component disabled.
	 * This prevents users from being able to interact with the button, and conveys its inactive state to assistive technologies.
	 */
	@Prop() readonly disabled: boolean = false
	/**
	 * Defines whether the accordion is open or closed by default
	 */
	@Prop({ attribute: "is-expanded" }) readonly isExpanded: boolean = false
	/**
	 * Used to change the HMTL tag in the accordion trigger for correct semantic structure
	 */
	@Prop({ attribute: "trigger-tag" }) readonly triggerTag: string = "h3"

	/**
	 * Called when the accordion state changes
	 */
	@Event({ eventName: "gov-change" }) govChange: EventEmitter<GovAccordionItemChangeEvent>

	@Watch("label")
	validateLabel(newValue: string): void {
		if (!newValue) {
			govErrorLog(`[${AccordionItemClass.root}]: Parameter label is required.`)
		}
	}

	@Watch("triggerTag")
	validateTriggerTag(newValue: string): void {
		const validTags = ["h1", "h2", "h3", "h4", "h5", "h6", "span"]
		if (newValue) {
			if (!validTags.includes(newValue)) {
				govErrorLog(`[${AccordionItemClass.root}]: Tag ${newValue} is not allowed.`)
			}
		}
	}

	@Watch("size")
	validateSize(newValue: string): void {
		validateProp(Sizes, newValue, AccordionItemClass.root)
	}

	@Watch("variant")
	validateVariant(newValue: string): void {
		validateProp(Variants, newValue, AccordionItemClass.root)
	}

	private emmitChange(): void {
		this.govChange.emit({ open: this.isExpandedState, element: this.host })
	}

	componentWillLoad(): void {
		this.isExpandedState = this.isExpanded
		this.validateSize(this.size)
		this.validateLabel(this.label)
		this.validateVariant(this.variant)
		this.validateTriggerTag(this.triggerTag)
	}

	render() {
		const triggerId = this.identifier || this.triggerId
		const contentId = this.contentId
		const { isExpandedState, label } = this
		const TriggerTag = this.triggerTag

		return (
			<Host class={AccordionItemClass.root} role="listitem">
				<button
					id={triggerId}
					class={AccordionItemClass.header}
					aria-expanded={booleanString(isExpandedState)}
					aria-controls={contentId}
					disabled={this.disabled}
					aria-disabled={toBoolAttrIfDefined(this.disabled)}
					onClick={() => this.toggle()}>
					<TriggerTag class={AccordionItemClass.title}>
						<slot name="icon" />
						<span class={AccordionItemClass.label}>
							{label}
						</span>

						<slot name="suffix" />
					</TriggerTag>

					<gov-icon name="arrow-down"></gov-icon>
				</button>
				<div
					class={AccordionItemClass.content}
					id={contentId}
					aria-hidden={booleanString(!isExpandedState)}
					hidden={!isExpandedState}
					aria-labelledby={triggerId}>
					<slot />
				</div>
			</Host>
		)
	}

	/**
	 * Opening the accordion
	 */
	@Method()
	async open(): Promise<void> {
		this.isExpandedState = true
		this.emmitChange()
	}

	/**
	 * Closing the accordion
	 */
	@Method()
	async close(): Promise<void> {
		this.isExpandedState = false
		this.emmitChange()
	}

	/**
	 * Accordion switch
	 */
	@Method()
	async toggle(): Promise<void> {
		this.isExpandedState = !this.isExpandedState
		this.emmitChange()
	}

	/**
	 * Returns the current state of the component
	 */
	@Method()
	async currentState(): Promise<boolean> {
		return this.isExpandedState
	}

}
